import { Icon, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useTenant } from 'src/contexts/TenantContext';

// URL to Dropbox Chooser SDK (v2)
const DROPBOX_SDK_URL = 'https://www.dropbox.com/static/api/2/dropins.js';
const SCRIPT_ID = 'dropboxjs';

/**
 * Track the promise so the SDK is fetched only once, even if multiple
 * components mount at the same time.
 */
let dropboxLoadPromise: Promise<void> | null = null;

function loadDropboxSdk(appKey: string): Promise<void> {
  // If running in SSR / non-browser environments, skip loading.
  if (typeof window === 'undefined') return Promise.resolve();

  // SDK already attached to window – we're done.
  if ((window as any).Dropbox) return Promise.resolve();

  // Reuse in-flight promise when possible.
  if (dropboxLoadPromise) return dropboxLoadPromise;

  dropboxLoadPromise = new Promise<void>((resolve, reject) => {
    // Check for an existing <script> tag that might be loading/loaded already.
    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject());
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = DROPBOX_SDK_URL;
    script.type = 'text/javascript';
    script.async = true;
    // Dropbox requires your app key to be provided as an attribute
    script.setAttribute('data-app-key', appKey);

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Dropbox SDK'));

    document.body.appendChild(script);
  });

  return dropboxLoadPromise;
}

// Replace with your actual Dropbox app key
export interface DropboxChooserProps {
  /** Callback fired when the user selects file(s) */
  success: (files: any[]) => void;
  /** Callback fired when the chooser is closed without selecting */
  cancel?: () => void;
  /** Callback fired when file processing starts (before download) */
  onProcessingStart?: (fileCount: number) => void;
  /** Link type for returned URLs (default: "preview") */
  linkType?: 'preview' | 'direct';
  /** Allow selecting multiple files at once (default: false) */
  multiselect?: boolean;
  /** Restrict selectable file extensions */
  extensions?: string[];
  /** Disable interaction entirely */
  disabled?: boolean;
  /** Custom trigger element */
  children?: React.ReactNode;
}

/**
 * A lightweight wrapper around the Dropbox Chooser SDK making it
 * straightforward to integrate the picker in React/TypeScript apps.
 */
const DropboxChooser: React.FC<DropboxChooserProps> = ({
  success,
  cancel = () => {},
  onProcessingStart,
  linkType = 'direct',
  multiselect = true,
  extensions,
  disabled = false,
}) => {
  const { getConfigValue } = useTenant();
  const DROPBOX_APP_ID: string = getConfigValue(
    'MEDIA_PROVIDER_DROPBOX_APP_KEY',
  );

  const onChoose = useCallback(() => {
    if (disabled) return;
    // The SDK may not be ready yet.
    if (!(window as any).Dropbox) return;

    (window as any).Dropbox.choose({
      success: (files: any[]) => {
        // Notify parent that processing is starting
        onProcessingStart?.(files.length);
        // Call original success callback
        success(files);
      },
      cancel,
      linkType,
      multiselect,
      extensions,
    });
  }, [disabled, success, cancel, linkType, multiselect, extensions, onProcessingStart]);

  useEffect(() => {
    loadDropboxSdk(DROPBOX_APP_ID).catch((err) => {
      console.error('[DropboxChooser] Failed to load Dropbox SDK', err);
    });
  }, []);

  return (
    <button
      type="button"
      onClick={onChoose}
      style={{
        background: 'none',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        height: 64,
        gap: '12px',
        paddingInline: '20px',
        flexDirection: 'row',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: 150,
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ width: 32, height: 32 }}>
        <img alt={'Dropbox'} src={'/icons/DropboxIcon.svg'} />
      </Icon>
      <Typography>Dropbox</Typography>
    </button>
  );
};

export default DropboxChooser;
